import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { X, ArrowUpRight } from 'lucide-react';
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  addMonths,
} from 'date-fns';
import { keyframes } from 'styled-components';
import Card from '../Card';
import { apiFetch } from '../../lib/api';
import {
  SCHEDULE_MEETING_TYPE_COLORS,
  SCHEDULE_MEETING_TYPE_LABELS,
  type ScheduleMeetingType,
} from '../../lib/schedule/meetingTypes';

type MeetingSummary = {
  totalIncludedMeetings: number;
  totalUsedMeetings: number;
  totalRemainingMeetings: number;
  unassignedMeetingsInWindow: number;
  contracts: Array<{
    serviceContractId: string;
    serviceCode: string | null;
    serviceName: string;
    tierName: string | null;
    tierSkuId: string | null;
    cadenceLabel: string;
    window: 'month' | 'quarter';
    windowLabel: string;
    periodStartAt: string;
    periodEndAt: string;
    includedMeetings: number;
    usedMeetings: number;
    remainingMeetings: number;
  }>;
};

type ClientScheduleEvent = {
  id: string;
  orgId?: string | null;
  serviceContractId?: string | null;
  serviceCode?: string | null;
  serviceName?: string | null;
  meetingType: ScheduleMeetingType;
  title: string;
  description?: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: 'requested' | 'scheduled' | 'canceled' | 'completed' | 'no_show';
  requestStatus?: 'pending' | 'confirmed' | null;
  requestedForDate?: string | null;
  requestedAt?: string | null;
  meetingLinks?: string[];
  attendees?: Array<{
    id: string;
    userId?: string | null;
    email?: string | null;
    name?: string | null;
    role?: string | null;
  }>;
  discordMeeting?: {
    scheduledEventId: string;
    scheduledEventUrl: string;
    voiceChannelName: string;
    voiceChannelUrl: string;
  } | null;
};

type Props = {
  orgId?: string | null;
  organizationName?: string | null;
  organizationLogoUrl?: string | null;
  meetingSummary?: MeetingSummary | null;
  meetingSummaryLoading?: boolean;
};

const scheduleAccent = '#e0a15e';

const skeletonPulse = keyframes`
  0% {
    opacity: 0.48;
  }
  50% {
    opacity: 0.9;
  }
  100% {
    opacity: 0.48;
  }
`;

const meetingDotPulse = keyframes`
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(224, 161, 94, 0.38);
    opacity: 0.88;
  }
  70% {
    transform: scale(1.14);
    box-shadow: 0 0 0 8px rgba(224, 161, 94, 0);
    opacity: 1;
  }
  100% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(224, 161, 94, 0);
    opacity: 0.88;
  }
`;

const bottomDrawerBounceIn = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(0, 34px, 0) scale(0.985);
  }
  58% {
    opacity: 1;
    transform: translate3d(0, -4px, 0) scale(1);
  }
  78% {
    transform: translate3d(0, 1px, 0) scale(1);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
`;

const bottomDrawerBounceOut = keyframes`
  0% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate3d(0, 26px, 0) scale(0.985);
  }
`;

const sideDrawerIn = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(28px, 0, 0);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const sideDrawerOut = keyframes`
  0% {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
  100% {
    opacity: 0;
    transform: translate3d(22px, 0, 0);
  }
`;

const overlayFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const overlayFadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const DRAWER_ANIMATION_MS = 440;

const Wrap = styled.section`
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1.65fr) minmax(300px, 0.95fr);
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const CalendarCard = styled(Card)`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 1rem;
  min-height: 0;
  padding: 1.1rem;

  @media (max-width: 560px) {
    gap: 0.8rem;
    padding: 0.85rem;
  }
`;

const OnboardingBanner = styled(Card)<{ $completed: boolean }>`
  position: relative;
  overflow: hidden;
  display: grid;
  min-height: 0;
  padding: 1.1rem;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(9, 12, 18, 0.34);
    opacity: ${({ $completed }) => ($completed ? 1 : 0)};
    transition: opacity 180ms ease;
    pointer-events: none;
    z-index: 0;
  }

  @media (max-width: 560px) {
    padding: 0.9rem;
  }
`;

const OnboardingBannerLeft = styled.div<{ $completed: boolean }>`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 0.4rem;
  min-width: 0;
  opacity: ${({ $completed }) => ($completed ? 0.62 : 1)};
  transition: opacity 180ms ease;
`;

const OnboardingBannerRight = styled.div<{ $completed: boolean }>`
  position: relative;
  z-index: 1;
  display: grid;
  align-content: start;
  justify-items: center;
  min-width: 0;
  opacity: ${({ $completed }) => ($completed ? 0.42 : 1)};
  transition: opacity 180ms ease;
`;

const OnboardingCompletedStamp = styled.div`
  position: absolute;
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%) rotate(-19deg);
  border: 2px solid rgba(166, 76, 68, 0.88);
  color: #a64c44;
  padding: 0.28rem 1rem 0.34rem;
  border-radius: 12px;
  font-size: clamp(1rem, 2.4vw, 1.3rem);
  font-weight: 900;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  background: rgba(166, 76, 68, 0.08);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.16);
  pointer-events: none;
  z-index: 2;
  white-space: nowrap;
`;

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  align-self: stretch;

  @media (max-width: 1100px) {
    display: grid;
    grid-template-rows: auto auto auto;
  }
`;

const PanelCard = styled(Card)`
  display: grid;
  gap: 0.9rem;
  min-height: 0;
  padding: 1.1rem;

  @media (max-width: 560px) {
    gap: 0.75rem;
    padding: 0.9rem;
  }
`;

const EventsPanelCard = styled(PanelCard)`
  flex: 1 1 0;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  overflow: hidden;
`;

const EventsPanelBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.2rem;
  display: grid;
  gap: 0.9rem;
  align-content: start;
`;

const CalendarTopBar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: start;

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.8rem;
  }

  @media (max-width: 560px) {
    gap: 0.7rem;
  }
`;

const SectionHeader = styled.div`
  display: grid;
  gap: 0.3rem;
`;

const CalendarTitleRow = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 760px) {
    flex-wrap: wrap;
  }
`;

const Eyebrow = styled.div`
  color: rgba(224, 161, 94, 0.76);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const Title = styled.h3`
  margin: 0;
  color: ${scheduleAccent};
  font-size: 1.55rem;
  font-weight: 900;
  line-height: 1.05;

  @media (max-width: 560px) {
    font-size: 1.28rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  color: rgba(232, 234, 237, 0.74);
  font-size: 0.9rem;
  line-height: 1.5;

  @media (max-width: 560px) {
    font-size: 0.78rem;
    line-height: 1.45;
  }
`;

const CalendarSubtitle = styled(Subtitle)`
  grid-column: 1 / -1;
  display: grid;
  gap: 0.3rem;
`;

const SubtitleInlineOrg = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  vertical-align: middle;
`;

const SubtitleOrgLogo = styled.span`
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 560px) {
    width: 1rem;
    height: 1rem;
  }
`;

const CalendarHeader = styled.div`
  display: grid;
  justify-items: end;
  align-content: start;
  gap: 0.55rem;

  @media (max-width: 760px) {
    justify-items: end;
  }
`;

const MonthLabel = styled.div`
  color: #f4f6fb;
  font-size: 1rem;
  font-weight: 900;
  text-align: right;

  @media (max-width: 560px) {
    font-size: 0.9rem;
  }
`;

const NavGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  justify-content: flex-end;

  @media (max-width: 560px) {
    gap: 0.45rem;
  }
`;

const NavButton = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  color: #f4f6fb;
  font-size: 0.96rem;
  font-weight: 900;
  cursor: pointer;

  @media (max-width: 560px) {
    width: 32px;
    height: 32px;
    border-radius: 9px;
    font-size: 0.86rem;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.55rem;

  @media (max-width: 560px) {
    gap: 0.28rem;
  }
`;

const DayHeader = styled.div`
  text-align: center;
  color: rgba(186, 198, 224, 0.82);
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 560px) {
    font-size: 0.56rem;
    letter-spacing: 0.04em;
  }
`;

const DayCell = styled.button<{ $selected: boolean; $outside: boolean; $today: boolean }>`
  min-height: 92px;
  padding: 0.65rem 0.55rem;
  border-radius: 14px;
  border: 1px solid
    ${({ $selected, $today }) =>
      $selected
        ? 'rgba(224, 161, 94, 0.78)'
        : $today
          ? 'rgba(166, 76, 68, 0.82)'
          : 'rgba(255, 255, 255, 0.06)'};
  background: ${({ $selected }) =>
    $selected ? 'rgba(224, 161, 94, 0.12)' : 'rgba(255, 255, 255, 0.02)'};
  color: #f4f6fb;
  display: grid;
  align-content: space-between;
  justify-items: center;
  gap: 0.45rem;
  cursor: pointer;
  opacity: ${({ $outside }) => ($outside ? 0.45 : 1)};
  transition:
    border-color 140ms ease,
    background 140ms ease,
    transform 140ms ease;

  &:hover {
    border-color: rgba(224, 161, 94, 0.4);
    background: rgba(224, 161, 94, 0.08);
    transform: translateY(-1px);
  }

  @media (max-width: 640px) {
    min-height: 80px;
  }

  @media (max-width: 560px) {
    min-height: 0;
    aspect-ratio: 1 / 1.08;
    padding: 0.35rem 0.16rem 0.3rem;
    border-radius: 12px;
    gap: 0.18rem;
    align-content: start;
    grid-template-rows: auto 1fr auto;
  }
`;

const DayContent = styled.div`
  width: 100%;
  display: grid;
  gap: 0.12rem;
  justify-items: center;
  text-align: center;

  @media (max-width: 560px) {
    gap: 0.08rem;
  }
`;

const DayNumber = styled.div<{ $today: boolean }>`
  color: ${({ $today }) => ($today ? '#f4b36d' : '#f4f6fb')};
  font-size: 0.92rem;
  font-weight: 900;

  @media (max-width: 560px) {
    font-size: 0.82rem;
  }
`;

const DayMeta = styled.div`
  color: rgba(186, 198, 224, 0.72);
  font-size: 0.7rem;
  font-weight: 800;
  text-align: center;
  line-height: 1.15;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-width: 100%;

  @media (max-width: 560px) {
    font-size: 0.54rem;
    -webkit-line-clamp: 1;
  }
`;

const DotRow = styled.div`
  display: flex;
  gap: 0.28rem;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
  align-items: center;
  justify-self: center;
  align-self: end;
  min-height: 12px;

  @media (max-width: 560px) {
    gap: 0.18rem;
    min-height: 8px;
    align-self: end;
  }
`;

const Dot = styled.span<{ $type: ScheduleMeetingType }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${({ $type }) => SCHEDULE_MEETING_TYPE_COLORS[$type]};
  animation: ${meetingDotPulse} 1.8s ease-in-out infinite;
  transform-origin: center;

  @media (max-width: 560px) {
    width: 6px;
    height: 6px;
  }
`;

const MetricWrap = styled.div`
  display: grid;
  gap: 0.3rem;
  padding: 0.25rem 0 0.1rem;
`;

const MetricValue = styled.div`
  color: #f4f6fb;
  font-size: clamp(2rem, 4vw, 2.7rem);
  font-weight: 900;
  line-height: 0.92;
`;

const MetricLabel = styled.div`
  color: rgba(232, 234, 237, 0.86);
  font-size: 0.9rem;
  font-weight: 800;
`;

const ContractList = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const ContractItem = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
  padding: 0.8rem 0.9rem;
  display: grid;
  gap: 0.35rem;
`;

const ContractTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`;

const ContractName = styled.div`
  color: #f4f6fb;
  font-size: 0.92rem;
  font-weight: 800;
`;

const ContractCount = styled.div`
  color: ${scheduleAccent};
  font-size: 0.8rem;
  font-weight: 900;
  white-space: nowrap;
`;

const ContractMeta = styled.div`
  color: rgba(186, 198, 224, 0.74);
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.45;
`;

const EventsList = styled.div`
  display: grid;
  gap: 0.75rem;
  align-content: start;
`;

const EventItem = styled.button`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
  padding: 0.85rem 0.95rem;
  display: grid;
  gap: 0.3rem;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 140ms ease,
    background 140ms ease,
    transform 140ms ease;

  &:hover {
    border-color: rgba(224, 161, 94, 0.3);
    background: rgba(224, 161, 94, 0.06);
    transform: translateY(-1px);
  }
`;

const EventTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const EventType = styled.div<{ $type: ScheduleMeetingType }>`
  color: ${({ $type }) => SCHEDULE_MEETING_TYPE_COLORS[$type]};
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const EventTime = styled.div`
  color: rgba(232, 234, 237, 0.78);
  font-size: 0.76rem;
  font-weight: 800;
`;

const EventTitle = styled.div`
  color: #f4f6fb;
  font-size: 0.92rem;
  font-weight: 800;
  line-height: 1.35;
`;

const EventMeta = styled.div`
  color: rgba(186, 198, 224, 0.72);
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.45;
`;

const EmptyState = styled.div`
  border-radius: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  color: rgba(186, 198, 224, 0.76);
  font-size: 0.84rem;
  line-height: 1.55;
  padding: 1rem;
`;

const HelperText = styled.div`
  color: rgba(186, 198, 224, 0.72);
  font-size: 0.76rem;
  line-height: 1.5;
`;

const DrawerOverlay = styled.button<{ $closing: boolean }>`
  position: fixed;
  inset: 0;
  border: none;
  padding: 0;
  margin: 0;
  background: rgba(8, 10, 14, 0.58);
  backdrop-filter: blur(4px);
  z-index: 1300;
  cursor: pointer;
  animation: ${({ $closing }) => ($closing ? overlayFadeOut : overlayFadeIn)}
    ${DRAWER_ANIMATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const DrawerShell = styled.aside<{ $closing: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: min(480px, calc(100vw - 1rem));
  height: 100vh;
  z-index: 1301;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.98), rgba(18, 20, 25, 0.98));
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: -20px 0 48px rgba(0, 0, 0, 0.42);
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
  animation: ${({ $closing }) => ($closing ? sideDrawerOut : sideDrawerIn)}
    ${DRAWER_ANIMATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 720px) {
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100vw;
    height: min(82vh, 720px);
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 24px 24px 0 0;
    box-shadow: 0 -22px 48px rgba(0, 0, 0, 0.42);
    transform-origin: bottom center;
    will-change: transform, opacity;
    animation: ${({ $closing }) => ($closing ? bottomDrawerBounceOut : bottomDrawerBounceIn)}
      ${DRAWER_ANIMATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }
`;

const DrawerHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: start;
  padding: 1rem 1rem 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  @media (max-width: 720px) {
    padding-top: 0.9rem;
  }
`;

const DrawerHeaderText = styled.div`
  display: grid;
  gap: 0.25rem;
  min-width: 0;
`;

const DrawerTitle = styled.div`
  color: ${scheduleAccent};
  font-size: 1.2rem;
  font-weight: 900;
  line-height: 1.1;
`;

const DrawerSubtitle = styled.div`
  color: rgba(232, 234, 237, 0.74);
  font-size: 0.82rem;
  line-height: 1.55;
  font-weight: 700;
`;

const DrawerClose = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #f4f6fb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DrawerBody = styled.div`
  overflow-y: auto;
  padding: 1rem;
  display: grid;
  align-content: start;
  gap: 1rem;
`;

const DrawerSection = styled.section`
  display: grid;
  gap: 0.75rem;
  min-width: 0;
`;

const DrawerSectionHeader = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const DrawerSectionTitle = styled.div`
  color: #f4f6fb;
  font-size: 0.96rem;
  font-weight: 900;
`;

const DrawerSectionMeta = styled.div`
  color: rgba(186, 198, 224, 0.72);
  font-size: 0.76rem;
  line-height: 1.5;
  font-weight: 700;
`;

const DrawerEventCard = styled.div<{ $active?: boolean }>`
  border-radius: 14px;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(224, 161, 94, 0.4)' : 'rgba(255, 255, 255, 0.06)'};
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.08)' : 'rgba(255, 255, 255, 0.03)'};
  padding: 0.9rem 0.95rem;
  display: grid;
  gap: 0.4rem;
`;

const DrawerEventLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const DrawerEventLink = styled.a`
  min-height: 30px;
  padding: 0 0.8rem;
  border-radius: 999px;
  border: 1px solid rgba(224, 161, 94, 0.24);
  background: rgba(224, 161, 94, 0.08);
  color: #f4f6fb;
  font-size: 0.74rem;
  font-weight: 800;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const DrawerField = styled.label`
  display: grid;
  gap: 0.45rem;
`;

const DrawerFieldLabel = styled.span`
  color: rgba(186, 198, 224, 0.78);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const DrawerTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  resize: vertical;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  color: #f4f6fb;
  padding: 0.85rem 0.95rem;
  font: inherit;
  line-height: 1.55;

  &::placeholder {
    color: rgba(186, 198, 224, 0.48);
  }

  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, 0.38);
    box-shadow: 0 0 0 1px rgba(224, 161, 94, 0.12);
  }
`;

const DrawerFormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const DrawerActionButton = styled.button`
  min-height: 40px;
  padding: 0 1rem;
  border-radius: 999px;
  border: 1px solid rgba(224, 161, 94, 0.24);
  background: linear-gradient(135deg, #79d4ff, #ffd2aa);
  color: #1b2230;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.01em;
  cursor: pointer;

  &:disabled {
    opacity: 0.48;
    cursor: not-allowed;
  }
`;

const DrawerSecondaryButton = styled.button`
  min-height: 38px;
  padding: 0 0.95rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  color: #f4f6fb;
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.48;
    cursor: not-allowed;
  }
`;

const OnboardingCallout = styled.div`
  color: rgba(186, 198, 224, 0.78);
  font-size: 0.92rem;
  font-weight: 900;
  letter-spacing: 0.01em;
  line-height: 1.4;
`;

const OnboardingStatus = styled.div<{ $tone: 'neutral' | 'accent' | 'good' }>`
  color: ${({ $tone }) =>
    $tone === 'good'
      ? '#7dd3a7'
      : $tone === 'accent'
        ? scheduleAccent
        : 'rgba(244, 246, 251, 0.82)'};
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const OnboardingMarquee = styled.div`
  width: 100%;
  min-height: calc(1.35em * 2);
  color: rgba(244, 246, 251, 0.94);
  font-size: clamp(1rem, 2vw, 1.12rem);
  font-weight: 900;
  line-height: 1.35;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OnboardingCaret = styled.span`
  display: inline-block;
  width: 1px;
  height: 1.02em;
  margin-left: 0.22rem;
  background: ${scheduleAccent};
  animation: ${skeletonPulse} 1s ease-in-out infinite;
  vertical-align: -0.08em;
`;

const SkeletonBlock = styled.div<{ $width?: string; $height?: string; $radius?: string }>`
  width: ${({ $width }) => $width ?? '100%'};
  height: ${({ $height }) => $height ?? '0.95rem'};
  border-radius: ${({ $radius }) => $radius ?? '10px'};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05),
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0.05)
  );
  animation: ${skeletonPulse} 1.4s ease-in-out infinite;
`;

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ONBOARDING_MARQUEE_MESSAGES = [
  'Set expectations',
  'Define deliverables',
  'Plan monthly client success meetings',
  'Grant access and permissions',
] as const;

function formatDateKey(date: Date) {
  return format(date, 'yyyy-MM-dd');
}

function parseEventDate(value: string): Date | null {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function toMeetingLinkHref(value: string) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function formatEventTimeRange(event: ClientScheduleEvent) {
  if (event.status === 'requested') return 'Request pending';
  const startsAt = parseEventDate(event.startsAt);
  const endsAt = parseEventDate(event.endsAt);
  if (!startsAt || !endsAt) return 'Time unavailable';
  return `${format(startsAt, 'h:mm a')} - ${format(endsAt, 'h:mm a')}`;
}

function normalizeClientScheduleEvent(raw: ClientScheduleEvent): ClientScheduleEvent {
  const requestStatus = raw.requestStatus === 'pending' || raw.requestStatus === 'confirmed'
    ? raw.requestStatus
    : null;
  return {
    ...raw,
    status: requestStatus === 'pending' ? 'requested' : raw.status,
    requestStatus,
    requestedForDate: raw.requestedForDate ?? null,
    requestedAt: raw.requestedAt ?? null,
    meetingLinks: Array.isArray(raw.meetingLinks) ? raw.meetingLinks : [],
  };
}

export default function ClientScheduleOverview({
  orgId,
  organizationName,
  organizationLogoUrl,
  meetingSummary,
  meetingSummaryLoading = false,
}: Props) {
  const [isCompactCalendar, setIsCompactCalendar] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 560 : false,
  );
  const [events, setEvents] = useState<ClientScheduleEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeCursor, setTimeCursor] = useState(() => Date.now());
  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerClosing, setDrawerClosing] = useState(false);
  const [activeDrawerEventId, setActiveDrawerEventId] = useState<string | null>(null);
  const [requestFormOpen, setRequestFormOpen] = useState(false);
  const [requestNotes, setRequestNotes] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestMessage, setRequestMessage] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [activeMarqueeIndex, setActiveMarqueeIndex] = useState(0);
  const [typedMarqueeLength, setTypedMarqueeLength] = useState(0);
  const [deletingMarquee, setDeletingMarquee] = useState(false);
  const drawerCloseTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (drawerCloseTimeoutRef.current !== null) {
        window.clearTimeout(drawerCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTimeCursor(Date.now());
    }, 30_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 560px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsCompactCalendar(event.matches);
    };

    handleChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (!orgId) {
      setEvents([]);
      setError(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const query = `?orgId=${encodeURIComponent(orgId)}&status=scheduled`;
        const response = await apiFetch<ClientScheduleEvent[]>(
          `/api/schedule/events${query}`,
        );
        if (!active) return;
        setEvents(
          Array.isArray(response)
            ? response.map(normalizeClientScheduleEvent)
            : [],
        );
      } catch (nextError) {
        if (!active) return;
        console.warn(
          '[ClientScheduleOverview] Failed to load schedule events',
          nextError,
        );
        setEvents([]);
        setError('Unable to load scheduled meetings right now.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [orgId]);

  const normalizedEvents = useMemo(
    () =>
      events
        .map((event) => {
          const startsAtDate = parseEventDate(event.startsAt);
          const endsAtDate = parseEventDate(event.endsAt);
          if (!startsAtDate || !endsAtDate) return null;
          return {
            ...event,
            startsAtDate,
            endsAtDate,
            dateKey: formatDateKey(startsAtDate),
          };
        })
        .filter(
          (
            event,
          ): event is ClientScheduleEvent & {
            startsAtDate: Date;
            endsAtDate: Date;
            dateKey: string;
          } => Boolean(event),
        )
        .sort((left, right) => left.startsAtDate.getTime() - right.startsAtDate.getTime()),
    [events],
  );

  const eventsByDay = useMemo(() => {
    const map = new Map<string, Array<(typeof normalizedEvents)[number]>>();
    normalizedEvents.forEach((event) => {
      const list = map.get(event.dateKey) ?? [];
      list.push(event);
      map.set(event.dateKey, list);
    });
    return map;
  }, [normalizedEvents]);

  const selectedDayKey = useMemo(() => formatDateKey(selectedDay), [selectedDay]);
  const selectedDayEvents = useMemo(
    () => eventsByDay.get(selectedDayKey) ?? [],
    [eventsByDay, selectedDayKey],
  );
  const activeDrawerEvent = useMemo(
    () =>
      activeDrawerEventId
        ? selectedDayEvents.find((event) => event.id === activeDrawerEventId) ?? null
        : null,
    [activeDrawerEventId, selectedDayEvents],
  );

  const upcomingEvents = useMemo(() => {
    const now = Date.now();
    return normalizedEvents.filter((event) => event.endsAtDate.getTime() >= now);
  }, [normalizedEvents]);

  const upcomingWeekEvents = useMemo(() => {
    const todayStart = startOfDay(new Date());
    const windowEnd = addDays(todayStart, 7);
    return upcomingEvents.filter(
      (event) =>
        event.startsAtDate.getTime() >= todayStart.getTime()
        && event.startsAtDate.getTime() < windowEnd.getTime(),
    );
  }, [upcomingEvents]);

  const onboardingEvents = useMemo(
    () =>
      normalizedEvents.filter((event) => event.meetingType === 'intro_call'),
    [normalizedEvents],
  );

  const onboardingEvent = useMemo(
    () =>
      onboardingEvents.find(
        (event) =>
          event.startsAtDate.getTime() <= timeCursor
          && event.endsAtDate.getTime() > timeCursor,
      )
      ?? onboardingEvents.find((event) => event.endsAtDate.getTime() >= timeCursor)
      ?? onboardingEvents[onboardingEvents.length - 1]
      ?? null,
    [onboardingEvents, timeCursor],
  );

  const onboardingStatus = useMemo(() => {
    if (!onboardingEvent) {
      return error
        ? {
            label: 'Unavailable',
            tone: 'neutral' as const,
            detail: 'Unable to load onboarding schedule right now',
          }
        : {
            label: 'Not scheduled',
            tone: 'neutral' as const,
            detail: 'Schedule your onboarding meeting today',
          };
    }

    if (onboardingEvent.status === 'requested') {
      return {
        label: 'Request pending',
        tone: 'accent' as const,
        detail: onboardingEvent.requestedForDate
          ? `Requested for ${format(
              onboardingEvent.startsAtDate,
              'EEEE, MMMM d, yyyy',
            )}`
          : 'Waiting for your account team to confirm the intro call.',
      };
    }

    if (onboardingEvent.status === 'canceled') {
      return {
        label: 'Canceled',
        tone: 'neutral' as const,
        detail: `Canceled for ${format(
          onboardingEvent.startsAtDate,
          'EEEE, MMMM d, yyyy',
        )}`,
      };
    }

    if (onboardingEvent.status === 'no_show') {
      return {
        label: 'No show',
        tone: 'neutral' as const,
        detail: `${format(
          onboardingEvent.startsAtDate,
          'EEEE, MMMM d, yyyy',
        )} • ${formatEventTimeRange(onboardingEvent)}`,
      };
    }

    if (
      onboardingEvent.status === 'completed'
      || onboardingEvent.endsAtDate.getTime() <= timeCursor
    ) {
      return {
        label: 'Completed',
        tone: 'neutral' as const,
        detail: `${format(
          onboardingEvent.startsAtDate,
          'EEEE, MMMM d, yyyy',
        )} • ${formatEventTimeRange(onboardingEvent)}`,
      };
    }

    if (onboardingEvent.startsAtDate.getTime() <= timeCursor) {
      return {
        label: 'In progress',
        tone: 'good' as const,
        detail: `Live now until ${format(onboardingEvent.endsAtDate, 'h:mm a')}`,
      };
    }

    return {
      label: 'Scheduled',
      tone: 'accent' as const,
      detail: `${format(
        onboardingEvent.startsAtDate,
        'EEEE, MMMM d, yyyy',
      )} • ${formatEventTimeRange(onboardingEvent)}`,
    };
  }, [error, onboardingEvent, timeCursor]);

  const onboardingCompleted = onboardingStatus.label === 'Completed';

  useEffect(() => {
    if (onboardingCompleted) {
      return undefined;
    }

    const activeMessage = ONBOARDING_MARQUEE_MESSAGES[activeMarqueeIndex];
    let timeoutId = 0;

    if (!deletingMarquee && typedMarqueeLength < activeMessage.length) {
      timeoutId = window.setTimeout(() => {
        setTypedMarqueeLength((current) => current + 1);
      }, 46);
    } else if (!deletingMarquee && typedMarqueeLength === activeMessage.length) {
      timeoutId = window.setTimeout(() => {
        setDeletingMarquee(true);
      }, 1400);
    } else if (deletingMarquee && typedMarqueeLength > 0) {
      timeoutId = window.setTimeout(() => {
        setTypedMarqueeLength((current) => Math.max(0, current - 1));
      }, 24);
    } else {
      timeoutId = window.setTimeout(() => {
        setActiveMarqueeIndex((current) => {
          if (ONBOARDING_MARQUEE_MESSAGES.length < 2) return current;
          let next = current;
          while (next === current) {
            next = Math.floor(Math.random() * ONBOARDING_MARQUEE_MESSAGES.length);
          }
          return next;
        });
        setDeletingMarquee(false);
      }, 220);
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeMarqueeIndex, deletingMarquee, onboardingCompleted, typedMarqueeLength]);

  const railEvents = upcomingWeekEvents;
  const railTitle = 'Upcoming meetings';
  const railSubtitle = 'Scheduled client-facing meetings for the next 7 days.';
  const dayHeaderLabels = isCompactCalendar
    ? ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    : DAY_HEADERS;

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(monthCursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(monthCursor), { weekStartsOn: 0 });
    const days: Date[] = [];
    let cursor = start;
    while (cursor <= end) {
      days.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return days;
  }, [monthCursor]);

  const totalRemainingMeetings = meetingSummary?.totalRemainingMeetings ?? 0;
  const totalIncludedMeetings = meetingSummary?.totalIncludedMeetings ?? 0;
  const totalUsedMeetings = meetingSummary?.totalUsedMeetings ?? 0;
  const contractRows = meetingSummary?.contracts ?? [];
  const marqueeText = ONBOARDING_MARQUEE_MESSAGES[activeMarqueeIndex].slice(
    0,
    typedMarqueeLength,
  );
  const isSelectedDayInPast = useMemo(
    () => selectedDay < startOfDay(new Date()),
    [selectedDay],
  );
  const selectedDayPendingRequest = useMemo(
    () => selectedDayEvents.find((event) => event.status === 'requested') ?? null,
    [selectedDayEvents],
  );

  const openDayDrawer = (day: Date, eventId?: string | null) => {
    if (drawerCloseTimeoutRef.current !== null) {
      window.clearTimeout(drawerCloseTimeoutRef.current);
      drawerCloseTimeoutRef.current = null;
    }
    setSelectedDay(day);
    setActiveDrawerEventId(eventId ?? null);
    setRequestError(null);
    setRequestMessage(null);
    setRequestFormOpen(false);
    setRequestNotes('');
    if (!isSameMonth(day, monthCursor)) {
      setMonthCursor(startOfMonth(day));
    }
    setDrawerClosing(false);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    if (!drawerVisible || drawerClosing) return;
    setDrawerClosing(true);
    if (drawerCloseTimeoutRef.current !== null) {
      window.clearTimeout(drawerCloseTimeoutRef.current);
    }
    drawerCloseTimeoutRef.current = window.setTimeout(() => {
      setDrawerVisible(false);
      setDrawerClosing(false);
      drawerCloseTimeoutRef.current = null;
    }, DRAWER_ANIMATION_MS);
  };

  const openEventDrawer = (
    event: ClientScheduleEvent & {
      startsAtDate: Date;
      endsAtDate: Date;
      dateKey: string;
    },
  ) => {
    openDayDrawer(event.startsAtDate, event.id);
  };

  const submitMeetingRequest = async () => {
    if (!orgId || requestSubmitting) return;
    try {
      setRequestSubmitting(true);
      setRequestError(null);
      setRequestMessage(null);
      const response = await apiFetch<ClientScheduleEvent>(
        '/api/schedule/events/request',
        {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgId,
          requestedDate: selectedDayKey,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          description: requestNotes,
          serviceContractId:
            contractRows.length === 1 ? contractRows[0]?.serviceContractId : undefined,
        }),
        },
      );
      const createdAt = parseEventDate(response?.startsAt ?? '');
      const created =
        response?.id && createdAt ? normalizeClientScheduleEvent(response) : null;
      if (!created || !createdAt) {
        throw new Error('Unable to request this meeting right now.');
      }
      setEvents((prev) =>
        [...prev, created].sort(
          (left, right) =>
            (parseEventDate(left.startsAt)?.getTime() ?? 0)
            - (parseEventDate(right.startsAt)?.getTime() ?? 0),
        ),
      );
      setRequestMessage('Meeting request submitted.');
      setActiveDrawerEventId(created.id);
      setRequestFormOpen(false);
      setRequestNotes('');
    } catch (nextError) {
      console.warn('[ClientScheduleOverview] Failed to submit meeting request', nextError);
      setRequestError(
        nextError instanceof Error
          ? nextError.message
          : 'Unable to submit this meeting request right now.',
      );
    } finally {
      setRequestSubmitting(false);
    }
  };

  return (
    <Wrap>
      <CalendarCard>
        <CalendarTopBar>
          <SectionHeader>
            <Eyebrow>Client Schedule</Eyebrow>
            <Title>Calendar</Title>
          </SectionHeader>

          <CalendarHeader>
            <MonthLabel>{format(monthCursor, 'MMMM yyyy')}</MonthLabel>
            <NavGroup>
              <NavButton
                type="button"
                aria-label="Previous month"
                onClick={() => setMonthCursor((current) => subMonths(current, 1))}
              >
                ‹
              </NavButton>
              <NavButton
                type="button"
                aria-label="Next month"
                onClick={() => setMonthCursor((current) => addMonths(current, 1))}
              >
                ›
              </NavButton>
            </NavGroup>
          </CalendarHeader>

          <CalendarSubtitle>
            {organizationName ? (
              <>
                <span>View scheduled meetings for</span>
                <SubtitleInlineOrg>
                  {organizationLogoUrl ? (
                    <SubtitleOrgLogo aria-hidden="true">
                      <img src={organizationLogoUrl} alt="" />
                    </SubtitleOrgLogo>
                  ) : null}
                  <span>{organizationName}</span>
                </SubtitleInlineOrg>
              </>
            ) : (
              'View your scheduled meetings.'
            )}
          </CalendarSubtitle>
        </CalendarTopBar>

        <CalendarGrid>
          {dayHeaderLabels.map((label, index) => (
            <DayHeader key={`${label}-${index}`}>{label}</DayHeader>
          ))}
          {calendarDays.map((day) => {
            const key = formatDateKey(day);
            const dayEvents = eventsByDay.get(key) ?? [];
            const isSelected = isSameDay(day, selectedDay);
            const outside = !isSameMonth(day, monthCursor);
            const isCurrentDay = isToday(day);
            const dayMetaLabel = isCompactCalendar
              ? ''
              : dayEvents.length > 0
                ? `${dayEvents.length} ${dayEvents.length === 1 ? 'meeting' : 'meetings'}`
                : 'No meetings';
            return (
              <DayCell
                key={key}
                type="button"
                $selected={isSelected}
                $outside={outside}
                $today={isCurrentDay}
                onClick={() => openDayDrawer(day)}
              >
                <DayContent>
                  <DayNumber $today={isCurrentDay}>{format(day, 'd')}</DayNumber>
                  {dayMetaLabel ? <DayMeta>{dayMetaLabel}</DayMeta> : null}
                </DayContent>
                <DotRow>
                  {dayEvents.slice(0, 4).map((event) => (
                    <Dot key={event.id} $type={event.meetingType} />
                  ))}
                </DotRow>
              </DayCell>
            );
          })}
        </CalendarGrid>
      </CalendarCard>

      <Rail>
        <OnboardingBanner $completed={onboardingCompleted}>
          <OnboardingBannerLeft $completed={onboardingCompleted}>
            <Eyebrow>Onboarding</Eyebrow>
            <Title>Onboarding Intro Call</Title>
            {loading ? (
              <SkeletonBlock $width="18rem" $height="1rem" />
            ) : (
              <>
                {!onboardingCompleted ? (
                  <OnboardingStatus $tone={onboardingStatus.tone}>
                    {onboardingStatus.label}
                  </OnboardingStatus>
                ) : null}
                <OnboardingCallout>{onboardingStatus.detail}</OnboardingCallout>
              </>
            )}
          </OnboardingBannerLeft>

          {!loading && onboardingCompleted ? (
            <OnboardingCompletedStamp>Completed</OnboardingCompletedStamp>
          ) : null}

          {!onboardingCompleted ? (
            <OnboardingBannerRight $completed={onboardingCompleted}>
              <OnboardingMarquee>
                <span>{marqueeText}</span>
                <OnboardingCaret />
              </OnboardingMarquee>
            </OnboardingBannerRight>
          ) : null}
        </OnboardingBanner>

        <PanelCard>
          <SectionHeader>
            <Eyebrow>Meeting Allowance</Eyebrow>
            <Title>Meetings</Title>
            <Subtitle>
              Remaining syncs based on the meeting cadence included in your active service contracts.
            </Subtitle>
          </SectionHeader>

          {meetingSummaryLoading ? (
            <>
              <MetricWrap>
                <SkeletonBlock $width="7rem" $height="3rem" $radius="14px" />
                <SkeletonBlock $width="10rem" $height="0.95rem" />
                <SkeletonBlock $width="82%" $height="0.85rem" />
              </MetricWrap>
              <ContractList>
                <ContractItem>
                  <ContractTitleRow>
                    <SkeletonBlock $width="55%" $height="0.95rem" />
                    <SkeletonBlock $width="4.5rem" $height="0.9rem" />
                  </ContractTitleRow>
                  <SkeletonBlock $width="62%" $height="0.8rem" />
                  <SkeletonBlock $width="48%" $height="0.8rem" />
                </ContractItem>
                <ContractItem>
                  <ContractTitleRow>
                    <SkeletonBlock $width="46%" $height="0.95rem" />
                    <SkeletonBlock $width="4rem" $height="0.9rem" />
                  </ContractTitleRow>
                  <SkeletonBlock $width="58%" $height="0.8rem" />
                  <SkeletonBlock $width="43%" $height="0.8rem" />
                </ContractItem>
              </ContractList>
            </>
          ) : (
            <>
              <MetricWrap>
                <MetricValue>{totalRemainingMeetings}</MetricValue>
                <MetricLabel>
                  {totalRemainingMeetings === 1 ? 'meeting remaining' : 'meetings remaining'}
                </MetricLabel>
               
              </MetricWrap>

            </>
          )}

          {!meetingSummaryLoading && meetingSummary?.unassignedMeetingsInWindow ? (
            <HelperText>
              {meetingSummary.unassignedMeetingsInWindow} scheduled
              {meetingSummary.unassignedMeetingsInWindow === 1 ? ' meeting is' : ' meetings are'} not
              yet tied to a specific contract.
            </HelperText>
          ) : null}
        </PanelCard>

        {!isCompactCalendar ? <EventsPanelCard>
          <SectionHeader>
            <Eyebrow>Events</Eyebrow>
            <Title>{railTitle}</Title>
            <Subtitle>{railSubtitle}</Subtitle>
          </SectionHeader>
          <EventsPanelBody>

          {error ? <EmptyState>{error}</EmptyState> : null}

          {!error && loading ? <EmptyState>Loading scheduled meetings...</EmptyState> : null}

          {!error && !loading && railEvents.length ? (
            <EventsList>
              {railEvents.map((event) => (
                <EventItem
                  key={event.id}
                  type="button"
                  onClick={() => openEventDrawer(event)}
                >
                  <EventTop>
                    <EventType $type={event.meetingType}>
                      {SCHEDULE_MEETING_TYPE_LABELS[event.meetingType]}
                    </EventType>
                    <EventTime>{formatEventTimeRange(event)}</EventTime>
                  </EventTop>
                  <EventTitle>{event.title}</EventTitle>
                  <EventMeta>
                    {format(event.startsAtDate, 'EEE, MMM d')}
                    {event.serviceName ? ` • ${event.serviceName}` : ''}
                  </EventMeta>
                </EventItem>
              ))}
            </EventsList>
          ) : null}

          {!error && !loading && !railEvents.length ? (
            <EmptyState>
              No scheduled meetings are on the calendar yet. Once your team books onboarding or sync
              meetings, they will appear here.
            </EmptyState>
          ) : null}
          </EventsPanelBody>
        </EventsPanelCard> : null}
      </Rail>

      {drawerVisible ? (
        <>
          <DrawerOverlay
            type="button"
            aria-label="Close schedule drawer"
            $closing={drawerClosing}
            onClick={closeDrawer}
          />
          <DrawerShell aria-label="Schedule details" $closing={drawerClosing}>
            <DrawerHeader>
              <DrawerHeaderText>
                <Eyebrow>Selected Day</Eyebrow>
                <DrawerTitle>{format(selectedDay, 'EEEE, MMMM d')}</DrawerTitle>
                <DrawerSubtitle>
                  {selectedDayEvents.length
                    ? `${selectedDayEvents.length} ${selectedDayEvents.length === 1 ? 'meeting item is' : 'meeting items are'} attached to this day.`
                    : 'No meeting items are attached to this day yet.'}
                </DrawerSubtitle>
              </DrawerHeaderText>
              <DrawerClose
                type="button"
                aria-label="Close schedule drawer"
                onClick={closeDrawer}
              >
                <X size={16} />
              </DrawerClose>
            </DrawerHeader>

            <DrawerBody>
              <DrawerSection>
                <DrawerSectionHeader>
                  <DrawerSectionTitle>Events</DrawerSectionTitle>
                  <DrawerSectionMeta>
                    Scheduled meetings and pending requests for this day.
                  </DrawerSectionMeta>
                </DrawerSectionHeader>

                {selectedDayEvents.length ? (
                  <EventsList>
                    {selectedDayEvents.map((event) => (
                      <DrawerEventCard
                        key={event.id}
                        $active={event.id === activeDrawerEvent?.id}
                      >
                        <EventTop>
                          <EventType $type={event.meetingType}>
                            {SCHEDULE_MEETING_TYPE_LABELS[event.meetingType]}
                          </EventType>
                          <EventTime>{formatEventTimeRange(event)}</EventTime>
                        </EventTop>
                        <EventTitle>{event.title}</EventTitle>
                        <EventMeta>
                          {event.serviceName ? `${event.serviceName} • ` : ''}
                          {event.timezone}
                        </EventMeta>
                        {event.description ? (
                          <EventMeta>{event.description}</EventMeta>
                        ) : null}
                        {event.meetingLinks?.length || event.discordMeeting ? (
                          <DrawerEventLinks>
                            {event.discordMeeting ? (
                              <DrawerEventLink
                                href={event.discordMeeting.scheduledEventUrl || event.discordMeeting.voiceChannelUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Discord meeting
                                <ArrowUpRight size={12} />
                              </DrawerEventLink>
                            ) : null}
                            {(event.meetingLinks ?? []).map((link) => {
                              const href = toMeetingLinkHref(link);
                              if (!href) return null;
                              return (
                                <DrawerEventLink
                                  key={href}
                                  href={href}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Meeting link
                                  <ArrowUpRight size={12} />
                                </DrawerEventLink>
                              );
                            })}
                          </DrawerEventLinks>
                        ) : null}
                      </DrawerEventCard>
                    ))}
                  </EventsList>
                ) : (
                  <EmptyState>
                    No scheduled meetings are attached to this day yet.
                  </EmptyState>
                )}
              </DrawerSection>

              {!isSelectedDayInPast ? (
                <DrawerSection>
                  <DrawerSectionHeader>
                    <DrawerSectionTitle>Request a meeting</DrawerSectionTitle>
                    <DrawerSectionMeta>
                      Submit a meeting request for this day and your account team will confirm the details.
                    </DrawerSectionMeta>
                  </DrawerSectionHeader>

                  {requestMessage ? <HelperText>{requestMessage}</HelperText> : null}
                  {requestError ? (
                    <HelperText style={{ color: '#fca5a5' }}>{requestError}</HelperText>
                  ) : null}

                  {selectedDayPendingRequest ? (
                    <HelperText>A meeting request is already pending for this day.</HelperText>
                  ) : meetingSummaryLoading ? (
                    <HelperText>Checking meeting availability...</HelperText>
                  ) : totalRemainingMeetings <= 0 ? (
                    <HelperText>No included meetings are available to request right now.</HelperText>
                  ) : requestFormOpen ? (
                    <>
                      <DrawerField>
                        <DrawerFieldLabel>Notes / description</DrawerFieldLabel>
                        <DrawerTextArea
                          value={requestNotes}
                          onChange={(event) => setRequestNotes(event.target.value)}
                          placeholder="Share context, goals, blockers, or what you need to cover in the meeting."
                        />
                      </DrawerField>
                      <DrawerFormActions>
                        <DrawerActionButton
                          type="button"
                          onClick={() => void submitMeetingRequest()}
                          disabled={requestSubmitting || !requestNotes.trim()}
                        >
                          {requestSubmitting ? 'Submitting...' : 'Submit request'}
                        </DrawerActionButton>
                        <DrawerSecondaryButton
                          type="button"
                          onClick={() => {
                            setRequestFormOpen(false);
                            setRequestNotes('');
                            setRequestError(null);
                          }}
                          disabled={requestSubmitting}
                        >
                          Cancel
                        </DrawerSecondaryButton>
                      </DrawerFormActions>
                    </>
                  ) : (
                    <DrawerActionButton
                      type="button"
                      onClick={() => {
                        setRequestFormOpen(true);
                        setRequestError(null);
                        setRequestMessage(null);
                      }}
                    >
                      Request meeting
                    </DrawerActionButton>
                  )}
                </DrawerSection>
              ) : null}

            </DrawerBody>
          </DrawerShell>
        </>
      ) : null}
    </Wrap>
  );
}
